// Real API service for Fitz-Net backend authentication
// In development, requests to /api/* are proxied to the actual backend via Vite
// In production, set VITE_API_BASE_URL to the full backend URL
import { API_URLS } from '../constants';
import { mockApi } from './mockApi';

const API_BASE_URL = API_URLS.FITZ_NET_API;
const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true';

const USERNAME_TAKEN_MESSAGE = 'That username is already taken. Usernames are not case-sensitive.';

/**
 * Pick the user-facing message for a failed request. A 409 means the username is taken
 * (case-insensitively), unless the backend supplied a message about email, which is kept.
 */
const resolveFailureMessage = (status, data, fallback) => {
  if (status === 409 && !(data.message && /email/i.test(data.message))) {
    return USERNAME_TAKEN_MESSAGE;
  }
  return data.message || fallback;
};

const parseResponseData = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      return {};
    }
  }

  try {
    const text = await response.text();
    return text ? { message: text } : {};
  } catch (error) {
    return {};
  }
};


/**
 * Create a new user account
 * @param {string} username - The username
 * @param {string} email - The user's email address
 * @param {string} password - The user's password
 * @returns {Promise<Object>} Response object with success status, message, and user data
 */
export const createUser = async (username, email, password) => {
  try {
    const url = `${API_BASE_URL}/user/create`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify({
        username,
        email,
        password,
      }),
    });

    const data = await parseResponseData(response);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: resolveFailureMessage(response.status, data, 'Failed to create user account'),
      };
    }

    return {
      success: true,
      message: data.message || 'Account created successfully',
      id: data.id,
      username: data.username,
      email: data.email,
    };
  } catch (_error) {
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

/**
 * Login with username and password
 * @param {string} username - The username
 * @param {string} password - The user's password
 * @returns {Promise<Object>} Response object with success status, message, user data, and token
 */
export const loginUser = async (username, password) => {
  if (USE_MOCK_API) {
    return mockApi.login(username, password);
  }

  try {
    const url = `${API_BASE_URL}/user/login`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify({
        username,
        password,
      }),
    });

    const data = await parseResponseData(response);

    if (!response.ok) {
      const authFallback = response.status === 401 || response.status === 403
        ? 'Invalid username or password'
        : 'Login failed';

      return {
        success: false,
        message: data.message || authFallback,
      };
    }

    return {
      success: data.success !== false,
      message: data.message || 'Login successful',
      username: data.username,
      email: data.email,
      token: data.token,
      boardColor: data.boardColor,
      permissions: data.permissions,
    };
  } catch (_error) {
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

/**
 * Validate JWT token
 * @param {string} token - The JWT token to validate
 * @returns {boolean} True if token is valid and not expired
 */
export const validateToken = (token) => {
  if (!token) return false;

  try {
    // Parse the token payload
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    // Check if token is expired
    return payload.exp > now;
  } catch (_error) {
    return false;
  }
};

/**
 * Logout (clear client-side session)
 * @returns {Promise<Object>} Response object with success status
 */
export const logoutUser = async () => {
  // For now, just clear client-side session
  // If backend has a logout endpoint in the future, we can call it here
  return {
    success: true,
    message: 'Logged out successfully',
  };
};

/**
 * Update user profile (username, email, password)
 * @param {Object} updates - Object containing username, email, and optional password
 * @param {string} updates.username - The new username
 * @param {string} updates.email - The new email
 * @param {string} [updates.password] - The new password (optional)
 * @param {string} token - The authentication token
 * @returns {Promise<Object>} Response object with success status and updated user data
 */
export const updateUserProfile = async (updates, token) => {
  if (USE_MOCK_API) {
    return mockApi.updateProfile(updates, token);
  }

  if (!token) {
    return {
      success: false,
      message: 'Authentication token is required',
    };
  }

  try {
    const url = `${API_BASE_URL}/user/update`;

    const requestBody = {
      username: updates.username,
      email: updates.email,
    };

    // Only include password if provided
    if (updates.password) {
      requestBody.password = updates.password;
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(requestBody),
    });

    const data = await parseResponseData(response);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: resolveFailureMessage(response.status, data, 'Failed to update profile'),
      };
    }

    return {
      success: true,
      message: data.message || 'Profile updated successfully',
      username: data.username,
      email: data.email,
      boardColor: data.boardColor,
      permissions: data.permissions,
    };
  } catch (_error) {
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

const authedRequest = async (path, token, { method = 'GET', body } = {}) => {
  if (!token) {
    return { success: false, message: 'Authentication token is required' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      mode: 'cors',
      credentials: 'omit',
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    const data = await parseResponseData(response);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: data.message || 'Request failed',
      };
    }

    return { success: true, data };
  } catch (_error) {
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

const MOCK_UNAVAILABLE = { success: false, message: 'Not available in mock mode' };

/**
 * Fetch the current user (including permissions) so permission changes apply
 * without logging in again.
 */
export const getCurrentUser = async (token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest('/user/me', token);
};

/** Admin: list defined permissions as [{ name, description }]. */
export const getAvailablePermissions = async (token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest('/admin/permissions', token);
};

/** Admin: define a new permission. */
export const createPermission = async (name, description, token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest('/admin/permissions', token, {
    method: 'POST',
    body: { name, description },
  });
};

/** Admin: delete a permission (revokes it from every user). */
export const deletePermission = async (name, token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest(`/admin/permissions/${encodeURIComponent(name)}`, token, {
    method: 'DELETE',
  });
};

/** Admin: list every user with their permissions. */
export const getAdminUsers = async (token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest('/admin/users', token);
};

/** Admin: replace the permission set of a user. */
export const setUserPermissions = async (username, permissions, token) => {
  if (USE_MOCK_API) return MOCK_UNAVAILABLE;
  return authedRequest(`/admin/users/${encodeURIComponent(username)}/permissions`, token, {
    method: 'PUT',
    body: { permissions },
  });
};

// Fixed messages for the statuses the Joenet proxy itself produces. Anything else
// is an upstream Radarr/Sonarr response, whose own error text is passed through.
const JOENET_STATUS_MESSAGES = {
  401: 'Joenet rejected the API key (or your session expired).',
  403: 'Your account does not have the JOENET_USER permission.',
  502: 'The Joenet host is unreachable.',
  503: 'Joenet is not configured on the server.',
};

/**
 * Call a Radarr/Sonarr v3 endpoint through the fitz-net-api proxy
 * ({GET,POST,PUT,DELETE} /joenet/{app}/{path}). The pasted API key travels only
 * in the X-Joenet-Key header - never in the URL - and is not logged.
 * Resolves { success, status, data } on 2xx, otherwise { success: false, status, message }.
 */
export const joenetRequest = async (app, path, token, key, { method = 'GET', query, body } = {}) => {
  if (USE_MOCK_API) return mockApi.joenetRequest(app, path, key, { method, query, body });

  if (!token) {
    return { success: false, message: 'Authentication token is required' };
  }
  if (!key) {
    return { success: false, status: 401, message: JOENET_STATUS_MESSAGES[401] };
  }

  const search = new URLSearchParams(query ?? {}).toString();
  const joiner = path.includes('?') ? '&' : '?';
  const url = `${API_BASE_URL}/joenet/${encodeURIComponent(app)}/${path}${search ? `${joiner}${search}` : ''}`;

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Joenet-Key': key,
      },
      mode: 'cors',
      credentials: 'omit',
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    const data = await parseResponseData(response);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message:
          JOENET_STATUS_MESSAGES[response.status] ||
          data?.message ||
          data?.[0]?.errorMessage ||
          `Request failed (${response.status})`,
      };
    }

    return { success: true, status: response.status, data };
  } catch (_error) {
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

export const api = {
  createUser,
  loginUser,
  validateToken,
  logoutUser,
  updateUserProfile,
  getCurrentUser,
  getAvailablePermissions,
  createPermission,
  deletePermission,
  getAdminUsers,
  setUserPermissions,
  joenetRequest,
};

export default api;

